export type ReplyPreviewReason =
    | 'to'
    | 'cc'
    | 'subscriber'
    | 'fallback_intake'
    | 'fallback_client'
    | 'fallback_explicit'
    | string;

export type NotifiedRecipientChannel = 'email' | 'signal';

export type NotifiedRecipientItem = {
    key: string;
    label: string;
    channel: NotifiedRecipientChannel;
    reasons: ReplyPreviewReason[];
};

type PreviewShape = {
    email?: {
        users?: Array<{ _id?: string; name?: string; email?: string; reasons?: string[] }>;
        external?: Array<string | { email?: string; reasons?: string[] }>;
    };
    signal?: {
        users?: Array<{ _id?: string; name?: string; email?: string; reasons?: string[] }>;
        groups?: Array<{ _id?: string; name?: string; reasons?: string[] }>;
    };
};

function normalizeExternal(item: string | { email?: string; reasons?: string[] }): {
    email: string;
    reasons: string[];
} | null {
    if (typeof item === 'string') {
        const email = item.trim();
        return email ? { email, reasons: [] } : null;
    }
    const email = String(item?.email || '').trim();
    if (!email)
        return null;
    return { email, reasons: Array.isArray(item.reasons) ? item.reasons : [] };
}

/** Flatten reply-preview payload into labeled chips with reason codes. */
export function buildNotifiedRecipientItems(preview: PreviewShape | null | undefined): NotifiedRecipientItem[] {
    if (!preview)
        return [];

    const items: NotifiedRecipientItem[] = [];
    const seen = new Set<string>();

    const push = (
        key: string,
        label: string,
        channel: NotifiedRecipientChannel,
        reasons: string[]
    ) => {
        const normalizedKey = key.toLowerCase();
        if (!normalizedKey || seen.has(normalizedKey))
            return;
        seen.add(normalizedKey);
        items.push({
            key: normalizedKey,
            label,
            channel,
            reasons: Array.from(new Set((reasons || []).filter(Boolean))),
        });
    };

    (preview.email?.users || []).forEach((user) => {
        const label = String(user?.name || user?.email || '').trim();
        const key = `email:${user?.email || user?._id || label}`.trim();
        push(key, label || key, 'email', user?.reasons || []);
    });

    (preview.email?.external || []).forEach((raw) => {
        const external = normalizeExternal(raw);
        if (!external)
            return;
        push(`email:${external.email}`, external.email, 'email', external.reasons);
    });

    (preview.signal?.users || []).forEach((user) => {
        const label = String(user?.name || user?.email || '').trim();
        const key = `signal-user:${user?._id || label}`;
        push(key, label || key, 'signal', user?.reasons || []);
    });

    (preview.signal?.groups || []).forEach((group) => {
        const label = String(group?.name || group?._id || '').trim();
        const key = `signal-group:${group?._id || label}`;
        push(key, label || key, 'signal', group?.reasons || []);
    });

    return items;
}

export function replyPreviewReasonI18nKey(reason: string): string {
    return `changes.reply_preview_reason_${reason}`;
}
